import { uiText } from "@/lib/i18n/uiText";
import { useState } from "react";
import { useNavigate, useLocation } from "@/lib/router-compat";
import { authPath } from "@/lib/authRedirect";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ConfidenceMeter } from "@/components/predictions/ConfidenceMeter";
import { RiskBadge } from "@/components/predictions/RiskBadge";
import { OddsBadge } from "@/components/predictions/OddsBadge";
import { ConfirmationDialog } from "@/components/shared/ConfirmationDialog";
import { cn } from "@/lib/utils";
import { Clock, ChevronRight, Check, X, RotateCcw, Trash2, Lock, Coins, Eye, Loader2, MessageSquare, CheckCircle2, Share2, Sparkles } from "lucide-react";
import { ShareDialog } from "@/components/share/ShareDialog";
import { AnalysisGate } from "@/components/predictions/RevealGate";
import { usePredictionGate } from "@/hooks/usePredictionGate";
import type { Prediction } from "@/types";
import { formatLocalTime } from "@/lib/formatLocalTime";
import { useUpdatePredictionOutcome, useDeletePredictions } from "@/hooks/usePredictions";
import { useAuth } from "@/hooks/useAuth";
import { useCoins } from "@/hooks/useCoins";
import { usePredictionReviews } from "@/hooks/usePredictionReviews";
import { useFoundingReviewedPredictions } from "@/hooks/useFoundingReviewedPredictions";
import { PredictionReviewDialog } from "@/components/founding/PredictionReviewDialog";
import { toast } from "@/hooks/use-toast";
import { brandError } from "@/lib/errors";
import { useLocalizedContent } from "@/hooks/useLocalizedContent";
import { FinalScore } from "@/components/predictions/FinalScore";
import { buildFixtureSlug } from "@/lib/entities";

interface PredictionCardProps {
  prediction: Prediction;
  variant?: "default" | "compact";
  onClick?: () => void;
  className?: string;
  showOutcomeControls?: boolean;
  showDeleteButton?: boolean;
}

export function PredictionCard({
  prediction,
  variant = "default",
  onClick,
  className,
  showOutcomeControls = true,
  showDeleteButton = true,
}: PredictionCardProps) {
  const { isAdmin, isFoundingMember } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const updateOutcome = useUpdatePredictionOutcome();
  const deletePrediction = useDeletePredictions();
  const { isLoggedIn } = useCoins();
  const { reviewedIds } = usePredictionReviews();
  const { reviewedIds: foundingReviewedIds } = useFoundingReviewedPredictions();
  const reviewedByFounder = foundingReviewedIds.has(prediction.id);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);

  // Localize the free-text explanation (prose only). English renders instantly;
  // other locales fill from the content_translations cache. Numbers/odds/team
  // names are preserved by the translator's glossary-constrained prompt.
  const { text: localizedExplanation } = useLocalizedContent(
    "prediction",
    prediction.id,
    "explanation",
    prediction.explanation,
  );


  // Played/evaluated predictions (won or lost) are NEVER gated — historical
  // results and Recent Winners are a marketing moat, free for everyone.
  // Upcoming picks route through the shared gate: one free reveal per UTC day,
  // then 2 credits. Analysis is always paid (see AnalysisGate below).
  const isEvaluated = prediction.was_correct === true || prediction.was_correct === false;
  const gate = usePredictionGate(prediction.id, { kind: "card", evaluated: isEvaluated, kickoff: prediction.kickoff_time });
  // Sharing must never expose more than the card does, so the share pipeline
  // reads the same gate state (card + analysis) the card itself renders with.
  const analysisGate = usePredictionGate(prediction.id, { kind: "analysis", evaluated: isEvaluated, kickoff: prediction.kickoff_time });
  const locked = gate.locked;

  const handleReveal = (e: React.MouseEvent) => {
    e.stopPropagation();
    gate.act();
  };


  const handleCardClick = () => {
    if (locked) return; // locked cards reveal via button only
    if (!prediction.predicted_outcome?.trim()) {
      toast({
        title: uiText("Prediction still being prepared"),
        description: uiText("Check back in 15 minutes — the prediction will be available then."),
      });
      return;
    }
    onClick?.();
  };

  const handleMarkOutcome = (wasCorrect: boolean | null, e: React.MouseEvent) => {
    e.stopPropagation();
    updateOutcome.mutate(
      {
        predictionId: prediction.id,
        wasCorrect,
        actualOutcome: wasCorrect === true ? prediction.predicted_outcome : undefined!,
      },
      {
        onSuccess: () => {
          toast({
            title: wasCorrect === null ? "Outcome reset" : wasCorrect ? "Marked as won" : "Marked as lost",
            description: `${prediction.team_home} vs ${prediction.team_away}`,
          });
        },
        onError: (error) => {
          toast({ title: "Statlign couldn't update this prediction", description: brandError(error), variant: "destructive" });
        },
      }
    );
  };

  const handleDeleteClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    deletePrediction.mutate([prediction.id], {
      onSuccess: () => {
        toast({ title: "Prediction deleted", description: `${prediction.team_home} vs ${prediction.team_away}` });
      },
      onError: (error) => {
        toast({ title: "Statlign couldn't delete this prediction", description: brandError(error), variant: "destructive" });
      },
    });
  };

  const RevealButton = ({ size = "default" }: { size?: "default" | "sm" }) => (
    <Button
      size={size === "sm" ? "sm" : "default"}
      onClick={handleReveal}
      disabled={gate.pending}
      className="gap-2 min-h-11"
    >
      {gate.pending ? (
        <Loader2 className="h-4 w-4" />
      ) : gate.freeAvailable ? (
        <Sparkles className="h-4 w-4" />
      ) : isLoggedIn ? (
        <Coins className="h-4 w-4" />
      ) : (
        <Lock className="h-4 w-4" />
      )}
      {uiText(gate.label)}
    </Button>
  );


  const OutcomeControls = () => {
    if (!isAdmin || !showOutcomeControls) return null;

    const isWon = prediction.was_correct === true;
    const isLost = prediction.was_correct === false;
    const isPending = prediction.was_correct === null || prediction.was_correct === undefined;

    return (
      <div className="flex items-center gap-1 mt-2" onClick={(e) => e.stopPropagation()}>
        <Button
          size="sm"
          variant={isWon ? "default" : "outline"}
          className={cn("h-9 min-w-9 px-3 text-xs gap-1", isWon && "bg-green-700 hover:bg-green-800 text-white")}
          onClick={(e) => handleMarkOutcome(true, e)}
          disabled={updateOutcome.isPending}
        >
          <Check className="h-3 w-3" />
           {uiText("Won")} </Button>
        <Button
          size="sm"
          variant={isLost ? "default" : "outline"}
          className={cn("h-9 min-w-9 px-3 text-xs gap-1", isLost && "bg-red-600 hover:bg-red-700 text-white")}
          onClick={(e) => handleMarkOutcome(false, e)}
          disabled={updateOutcome.isPending}
        >
          <X className="h-3 w-3" />
           {uiText("Lost")} </Button>
        {!isPending && (
          <Button
            size="sm"
            variant="ghost"
            className="h-9 min-w-9 px-3 text-xs gap-1"
            onClick={(e) => handleMarkOutcome(null, e)}
            disabled={updateOutcome.isPending}
          >
            <RotateCcw className="h-3 w-3" />
             {uiText("Reset")} </Button>
        )}
        {showDeleteButton && (
          <Button
            size="sm"
            variant="ghost"
            className="h-9 min-w-9 px-3 text-xs gap-1 text-destructive hover:text-destructive hover:bg-destructive/10"
            onClick={handleDeleteClick}
            disabled={deletePrediction.isPending}
            aria-label={uiText("Delete prediction")}
          >
            <Trash2 className="h-3 w-3" />
          </Button>
        )}
      </div>
    );
  };

  const OutcomeStatus = () => {
    if (/void/i.test(prediction.actual_outcome ?? "")) return <Badge variant="outline" className="border-warning text-warning">{uiText("Void (stake returned)")}</Badge>;
    if (prediction.was_correct === true) return <Badge className="bg-green-700 text-white">{uiText("Won")}</Badge>;
    if (prediction.was_correct === false) return <Badge className="bg-red-600 text-white">{uiText("Lost")}</Badge>;
    return <Badge variant="secondary">{uiText("Pending")}</Badge>;
  };
  const OutcomeBadge = () => (
    <span className="inline-flex items-center gap-1.5">
      <FinalScore
        matchId={prediction.match_id}
        actualOutcome={prediction.actual_outcome}
        graded={prediction.was_correct != null || /void/i.test(prediction.actual_outcome ?? "")}
      />
      <OutcomeStatus />
    </span>
  );

  // Founding members can review revealed predictions directly from the card.
  const ReviewControl = () => {
    if (!isFoundingMember) return null;
    const reviewed = reviewedIds.has(prediction.id);
    if (reviewed) {
      return (
        <Badge variant="outline" className="gap-1 text-emerald-500">
          <CheckCircle2 className="h-3.5 w-3.5" />  {uiText("Reviewed")} </Badge>
      );
    }
    return (
      <Button
        size="sm"
        variant="outline"
        className="h-7 gap-1.5 text-xs"
        onClick={(e) => {
          e.stopPropagation();
          setReviewDialogOpen(true);
        }}
      >
        <MessageSquare className="h-3.5 w-3.5" />  {uiText("Review (+20)")} </Button>
    );
  };


  /* -------------------- COMPACT -------------------- */
  if (variant === "compact") {
    return (
      <>
        <Card
          className={cn(
            "p-4 prediction-card border-border/50 bg-card/50",
            !locked && "cursor-pointer",
            className
          )}
          onClick={handleCardClick}
        >
          <div className="flex items-center justify-between gap-4">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 text-sm">
                <span className="font-semibold truncate">{prediction.team_home}</span>
                <span className="text-muted-foreground">{uiText("vs")}</span>
                <span className="font-semibold truncate">{prediction.team_away}</span>
              </div>
              <a
                href={`/fixtures/${buildFixtureSlug(prediction.team_home ?? "", prediction.team_away ?? "", prediction.kickoff_time)}`}
                onClick={(e) => e.stopPropagation()}
                className="mt-1 inline-block text-xs text-primary hover:underline"
              >
                {uiText("Match page")} →
              </a>
              <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                <Clock className="h-3 w-3" />
                {formatLocalTime(prediction.kickoff_time)}
                <span>·</span>
                <span>{prediction.league}</span>
              </div>
              {reviewedByFounder && (
                <Badge
                  variant="outline"
                  className="mt-1.5 w-fit max-w-full inline-flex items-center gap-1 whitespace-nowrap text-[10px] leading-tight px-2 py-0.5 border-primary/40 text-primary"
                >
                  <CheckCircle2 className="h-3 w-3 shrink-0" />
                  <span className="truncate">{uiText("Reviewed by Founding Member")}</span>
                </Badge>
              )}
              <OutcomeControls />
            </div>
            {locked ? (
              <RevealButton size="sm" />
            ) : (
              <div className="flex items-center gap-3">
                <Badge variant="secondary" className="text-xs">{uiText(prediction.market_type)}</Badge>
                <OutcomeBadge />
                <OddsBadge odds={prediction.odds} size="sm" />
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </div>
            )}
          </div>
        </Card>

        <ConfirmationDialog
          open={deleteDialogOpen}
          onOpenChange={setDeleteDialogOpen}
          title={uiText("Delete Prediction")}
          description={uiText("Are you sure you want to delete the prediction for {{v0}} vs {{v1}}? This action cannot be undone.", { v0: prediction.team_home, v1: prediction.team_away })}
          confirmText={uiText("Delete")}
          onConfirm={handleConfirmDelete}
          variant="destructive"
          isLoading={deletePrediction.isPending}
        />
      </>
    );
  }

  /* -------------------- LOCKED (default) -------------------- */
  if (locked) {
    return (
      <Card
        className={cn(
          "relative overflow-hidden prediction-card border-border/50 bg-card/80 backdrop-blur-xs h-full flex flex-col",
          className
        )}
      >
        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-primary/10 to-transparent rounded-bl-full" />
        <div className="relative p-5">
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <Badge variant="outline" className="text-xs border-primary/30 text-primary">
                  {prediction.league}
                </Badge>
                {reviewedByFounder && (
                  <Badge
                    variant="outline"
                    className="w-fit max-w-full inline-flex items-center gap-1 whitespace-nowrap text-[10px] leading-tight px-2 py-0.5 border-primary/40 text-primary"
                  >
                    <CheckCircle2 className="h-3 w-3 shrink-0" />
                    <span className="truncate">{uiText("Reviewed by Founding Member")}</span>
                  </Badge>
                )}
              </div>
              <div className="text-lg font-bold">
                <span>{prediction.team_home}</span>
                <span className="text-muted-foreground mx-2">{uiText("vs")}</span>
                <span>{prediction.team_away}</span>
              </div>
              <a
                href={`/fixtures/${buildFixtureSlug(prediction.team_home ?? "", prediction.team_away ?? "", prediction.kickoff_time)}`}
                onClick={(e) => e.stopPropagation()}
                className="mt-1 inline-block text-xs text-primary hover:underline"
              >
                {uiText("Match page")} →
              </a>
            </div>
            <div className="rounded-full bg-muted/60 p-2">
              <Lock className="h-5 w-5 text-muted-foreground" />
            </div>
          </div>

          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-4">
            <Clock className="h-4 w-4" />
            {formatLocalTime(prediction.kickoff_time)}
          </div>

          {/* Blurred teaser of hidden content to build suspense */}
          <div className="relative rounded-lg border border-border/50 bg-muted/30 p-4 overflow-hidden">
            <div className="space-y-3 blur-xs select-none pointer-events-none" aria-hidden>
              <div className="flex items-center justify-between">
                <div className="h-4 w-24 rounded bg-foreground/20" />
                <div className="h-6 w-16 rounded bg-primary/30" />
              </div>
              <div className="h-2 w-full rounded bg-foreground/15" />
              <div className="h-2 w-2/3 rounded bg-foreground/15" />
            </div>
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 bg-card/40 backdrop-blur-[1px]">
              <p className="text-xs font-medium text-foreground/80">{uiText("Prediction, odds & analysis hidden")}</p>
              <RevealButton />
            </div>
          </div>

          <OutcomeControls />
        </div>
      </Card>
    );
  }

  /* -------------------- UNLOCKED (default) -------------------- */
  return (
    <>
      <Card
        className={cn(
          "relative overflow-hidden cursor-pointer prediction-card border-border/50 bg-card/80 backdrop-blur-xs h-full flex flex-col",
          "hover:border-primary/30 transition-all duration-300",
          className
        )}
        onClick={handleCardClick}
      >
        <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-primary/10 to-transparent rounded-bl-full" />

        <div className="relative p-5 flex-1 flex flex-col">
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <Badge variant="outline" className="text-xs border-primary/30 text-primary">
                  {prediction.league}
                </Badge>
                <Badge variant="outline" className="text-xs border-emerald-500/40 text-emerald-500 gap-1">
                  <Eye className="h-3 w-3" />  {uiText("Revealed")} </Badge>
                {isAdmin && prediction.status === "pending" && (
                  <Badge variant="outline" className="text-xs border-amber-500/40 text-amber-500">
                     {uiText("Pending review")} </Badge>
                )}
                {reviewedByFounder && (
                  <Badge
                    variant="outline"
                    className="w-fit max-w-full inline-flex items-center gap-1 whitespace-nowrap text-[10px] leading-tight px-2 py-0.5 border-primary/40 text-primary"
                  >
                    <CheckCircle2 className="h-3 w-3 shrink-0" />
                    <span className="truncate">{uiText("Reviewed by Founding Member")}</span>
                  </Badge>
                )}
              </div>
              <div className="text-lg font-bold">
                <span>{prediction.team_home}</span>
                <span className="text-muted-foreground mx-2">{uiText("vs")}</span>
                <span>{prediction.team_away}</span>
              </div>
              <a
                href={`/fixtures/${buildFixtureSlug(prediction.team_home ?? "", prediction.team_away ?? "", prediction.kickoff_time)}`}
                onClick={(e) => e.stopPropagation()}
                className="mt-1 inline-block text-xs text-primary hover:underline"
              >
                {uiText("Match page")} →
              </a>
            </div>
            <OddsBadge odds={prediction.odds} size="lg" animated />
          </div>

          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{uiText("Prediction")}</p>
                <p className="font-semibold text-primary">{uiText(prediction.predicted_outcome)}</p>
              </div>
              <Badge variant="secondary" className="bg-secondary/80">{uiText(prediction.market_type)}</Badge>
            </div>

            <ConfidenceMeter score={prediction.confidence_score} size="md" />

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Clock className="h-4 w-4" />
                {formatLocalTime(prediction.kickoff_time)}
              </div>
              <div className="flex items-center gap-2">
                <OutcomeBadge />
                <RiskBadge level={prediction.risk_level} size="md" />
              </div>
            </div>

            <OutcomeControls />

            {isFoundingMember && (
              <div className="flex items-center gap-2 pt-1" onClick={(e) => e.stopPropagation()}>
                <ReviewControl />
              </div>
            )}

            {prediction.explanation && (
              <AnalysisGate predictionId={prediction.id} evaluated={isEvaluated} kickoff={prediction.kickoff_time} className="mt-3">
                <p className="text-sm text-muted-foreground line-clamp-2 border-t border-border/50 pt-3">
                  {localizedExplanation}
                </p>
              </AnalysisGate>
            )}


            <div className="pt-1" onClick={(e) => e.stopPropagation()}>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 gap-1.5 text-xs text-muted-foreground hover:text-foreground"
                disabled={gate.pending}
                title={gate.locked ? uiText("Reveal this pick before sharing it") : undefined}
                onClick={(e) => {
                  e.stopPropagation();
                  // A locked pick is not shareable: the button reveals first.
                  if (gate.locked) {
                    gate.act();
                    return;
                  }
                  setShareDialogOpen(true);
                }}
              >
                <Share2 className="h-3.5 w-3.5" /> {gate.locked ? uiText("Reveal to share") : uiText("Share")}
              </Button>
            </div>
          </div>
        </div>
      </Card>

      <ShareDialog
        prediction={prediction}
        open={shareDialogOpen}
        onOpenChange={setShareDialogOpen}
        revealed={!gate.locked}
        analysisRevealed={!analysisGate.locked}
      />

      <ConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title={uiText("Delete Prediction")}
        description={uiText("Are you sure you want to delete the prediction for {{v0}} vs {{v1}}? This action cannot be undone.", { v0: prediction.team_home, v1: prediction.team_away })}
        confirmText={uiText("Delete")}
        onConfirm={handleConfirmDelete}
        variant="destructive"
        isLoading={deletePrediction.isPending}
      />

      {isFoundingMember && (
        <PredictionReviewDialog
          open={reviewDialogOpen}
          onOpenChange={setReviewDialogOpen}
          prediction={{
            id: prediction.id,
            team_home: prediction.team_home,
            team_away: prediction.team_away,
            market_type: prediction.market_type,
          }}
        />
      )}
    </>
  );
}
